export const DEFAULT_RETURN_TARGET = '/'
export const LOGIN_ROUTE = '/login'

/** Control characters, space and DEL: never legitimate inside a return target. */
function hasUnsafeCharacter(value: string): boolean {
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0
    if (code <= 0x20 || code === 0x7f) {
      return true
    }
  }
  return false
}

/**
 * Reduce an untrusted `?redirect=` value to a safe *internal* target.
 *
 * The return target is attacker-controlled: it arrives in the URL of the
 * required-password-change page. Anything that is not unambiguously a path
 * inside this application collapses to `/`, so completing a password change
 * never sends the user off-origin.
 *
 * Rejected: absolute URLs, protocol-relative `//host`, backslash forms that
 * browsers fold into `//host`, control characters, and the login route itself
 * (which would otherwise loop).
 */
export function safeReturnTarget(raw: string | null | undefined): string {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 512) {
    return DEFAULT_RETURN_TARGET
  }

  // A backslash is never legitimate in one of our paths, and browsers treat it
  // as a slash in the authority position, so `/\evil.test` can escape origin.
  if (raw.includes('\\')) {
    return DEFAULT_RETURN_TARGET
  }

  // Control characters can be used to smuggle a second target past a naive
  // prefix check.
  if (hasUnsafeCharacter(raw)) {
    return DEFAULT_RETURN_TARGET
  }

  if (!raw.startsWith('/') || raw.startsWith('//')) {
    return DEFAULT_RETURN_TARGET
  }

  // Resolve against a throwaway base and confirm the value did not introduce
  // its own origin. This catches forms the string checks above do not model.
  const base = 'http://return-target.invalid'
  let url: URL
  try {
    url = new URL(raw, base)
  } catch {
    return DEFAULT_RETURN_TARGET
  }
  if (url.origin !== base) {
    return DEFAULT_RETURN_TARGET
  }

  if (url.pathname === LOGIN_ROUTE) {
    return DEFAULT_RETURN_TARGET
  }

  return `${url.pathname}${url.search}${url.hash}`
}
